import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SelectItem } from '@openng/optimus-ui/api';
import { forkJoin, Subscription } from 'rxjs';
import { COMMERCIAL_FACILITY_DETAIL_CONTAINER } from 'src/app/constants/common.constants';
import { NEW_COMMERCIAL_FACILITY } from 'src/app/constants/commercial-constants';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import {
  toSelectItems,
  trueFalseOptions,
} from 'src/app/core/functions/common-functions';
import { TabService } from 'src/app/core/services/tab.service';
import { CommercialFacilitySearch } from 'src/app/model/commercial-services/commercial-facility';

import { AorApiService } from 'src/app/services/api/aor-api.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { CommercialFacilityApiService } from 'src/app/services/api/commercial-facility-api.service';
import { CommercialFacilityBuilderService } from 'src/app/services/builder/commercial-facility-builder.service';
import { CommercialFacilityTableService } from 'src/app/services/table/commercial-facility-table.service';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { BreadCrumb } from 'src/app/model/common/bread-crumb';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { getCommercialFacilityTableColumns } from '../../definitions/commercial-facilities-table-definition';

@Component({
  selector: 'app-commercial-facilities-search-container',
  templateUrl: './commercial-facilities-search-container.component.html',
  styleUrls: ['./commercial-facilities-search-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BlockUI,
    ButtonDirective,
    ProgressSpinnerComponent,
    GeneralGridComponent,
    TranslatePipe,
  ],
})
export class CommercialFacilitiesSearchContainerComponent
  extends SearchContainerComponent<CommercialFacilitySearch>
  implements OnInit, OnDestroy
{
  protected _translateService = inject(TranslateService);
  protected _breadCrumbBuilderService = inject(BreadCrumbBuilderService);
  protected _commercialFacilityApi = inject(CommercialFacilityApiService);
  protected _commercialFacilityBuilder = inject(
    CommercialFacilityBuilderService
  );
  protected _commercialFacilityTableService = inject(
    CommercialFacilityTableService
  );
  protected _authApi = inject(AuthApiService);
  protected _tabService = inject(TabService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _aorApi = inject(AorApiService);

  readonly canAddCommercialFacility = signal(
    this._authApi.doesUserHaveClaim(
      CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_FACILITIES
        .CREATE_COMMERCIAL_FACILITIES
    )
  );
  availableCompanies: SelectItem[] = [];
  availableFacilityTypes: SelectItem[] = [];
  availableAORs: SelectItem[] = [];
  availableOperatingAreas: SelectItem[] = [];
  availableMarketAreas: SelectItem[] = [];
  scrollHeight = 'calc(100vh - 256px)';
  activeTabChangedSub?: Subscription;

  ngOnInit(): void {
    this.setTableService(this._commercialFacilityTableService);
    this.setupActiveTabChangedSubscription();
    this.initialize();
  }

  ngOnDestroy(): void {
    this.activeTabChangedSub?.unsubscribe();
  }

  private setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (tab?.header === this.screenName) {
          this.refresh();
        }
      }
    );
  }

  initialize(): void {
    if (this._deviceService.isMobile()) {
      this.scrollHeight = 'calc(100vh - 180px)';
    }

    const label = this._translateService.instant(
      'COMMERCIAL_SERVICES.SCREEN.COMMERCIAL_FACILITIES'
    );
    if (this._deviceService.isMobile()) {
      this._breadCrumbBuilderService.resetAndAddBreadCrumb(
        new BreadCrumb(label, null, false)
      );
    } else {
      this._tabService.updateActiveTabLabel(label);
    }
    this.screenName = label;

    forkJoin({
      companies: this._commercialFacilityApi.getCommercialCompanies(),
      facilityTypes: this._commercialFacilityApi.getCommercialFacilityTypes(),
      aors: this._aorApi.getAORs(null),
      operatingAreas: this._commercialFacilityApi.getCommercialOperatingAreas(),
      marketAreas: this._commercialFacilityApi.getCommercialMarketAreas(),
    }).subscribe(
      ({ companies, facilityTypes, aors, operatingAreas, marketAreas }) => {
        const choose = this._translateService.instant('COMMON.LABEL.CHOOSE');
        this.availableCompanies = toSelectItems(companies, choose);
        this.availableFacilityTypes = toSelectItems(facilityTypes, choose);
        this.availableAORs = toSelectItems(aors, choose);
        this.availableOperatingAreas = toSelectItems(operatingAreas, choose);
        this.availableMarketAreas = toSelectItems(marketAreas, choose);
        this.columns = getCommercialFacilityTableColumns(
          this.availableCompanies,
          this.availableFacilityTypes,
          this.availableAORs,
          this.availableOperatingAreas,
          this.availableMarketAreas,
          trueFalseOptions(this._translateService),
          this._deviceService.isMobile()
        );
        this.applyPreviousFilters();
        this.applyPreviousData();
      }
    );
  }

  changeViewEvent($event: { data: CommercialFacilitySearch }): void {
    const tab = this._tabService.buildNewTab(
      COMMERCIAL_FACILITY_DETAIL_CONTAINER,
      true,
      undefined,
      $event.data.id
    );
    if (this._deviceService.isMobile()) {
      this._tabService.setMobileTab(tab);
    } else {
      this._tabService.openTab(tab);
    }
  }

  private queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ): void {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'facilityName');

    this._commercialFacilityApi
      .queryForCommercialFacilities(req.pageSize, req.page, query, sort)
      .subscribe({
        next: (data) => {
          this.elements = [...data.items];
          this.finalizeQuery(data.totalRecords);
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey:
              'COMMERCIAL_SERVICES.MESSAGES.ERROR.LOAD_COMMERCIAL_FACILITIES',
          });
          this.loading = false;
        },
      });
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ): void {
    const query =
      this._commercialFacilityBuilder.buildCommercialFacilityQuery(filters);
    this.queryNetwork(req, $event, query);
  }

  addNewCommercialFacility(): void {
    this._commercialFacilityTableService.setSelected(null);
    const tab = this._tabService.buildNewTab(
      COMMERCIAL_FACILITY_DETAIL_CONTAINER,
      true,
      undefined,
      NEW_COMMERCIAL_FACILITY
    );
    if (this._deviceService.isMobile()) {
      this._tabService.setMobileTab(tab);
    } else {
      this._tabService.openTab(tab);
    }
  }

  refresh(): void {
    this.lazyLoad({
      lazy: this._commercialFacilityTableService.getLastLazyLoad()!,
    });
  }
}
